import { useCallback, useEffect, useRef, useState } from "react";
import { normaliseAlert } from "./alerts.js";

// Unauthenticated live-alert stream — used by the mobile shell (App.jsx),
// which has no login/JWT of its own. Connects to `url`, calls `onAlert`
// for each incoming alert message, and reconnects with exponential
// backoff (1s -> 2s -> ... capped at 30s, reset to 1s on a successful
// open) until unmounted.
export function useAlertStream(url, onAlert) {
  const reconnectDelay = useRef(1000);
  const ws = useRef(null);
  const dead = useRef(false);
  const onAlertRef = useRef(onAlert);
  useEffect(() => { onAlertRef.current = onAlert; }, [onAlert]);

  useEffect(() => {
    dead.current = false;
    function connect() {
      if (dead.current) return;
      ws.current = new WebSocket(url);

      ws.current.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "alert" && msg.data) onAlertRef.current(normaliseAlert(msg.data));
        } catch { /* ignore malformed frames */ }
      };

      ws.current.onopen = () => { reconnectDelay.current = 1000; };

      ws.current.onclose = () => {
        if (dead.current) return;
        setTimeout(connect, reconnectDelay.current);
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
      };

      ws.current.onerror = () => ws.current?.close();
    }
    connect();
    return () => { dead.current = true; ws.current?.close(); };
  }, [url]);
}

// Authenticated live-alert stream — used by the desktop shell
// (DesktopApp.jsx). Adds a 30s heartbeat ping (keeps the connection alive
// through NATs/proxies) and a 4401 close-code short-circuit: the server
// uses 4401 to mean "your JWT is no longer valid" — on that code we stop
// reconnecting and call onAuthError so the caller can refresh the token
// and re-enable, rather than looping against a socket that will keep
// rejecting us. Exposes wsStatus/connectedAt for a UI indicator.
export function useAuthenticatedAlertStream({ url, onAlert, onAuthError, enabled = true }) {
  const ws = useRef(null);
  const dead = useRef(false);
  const delay = useRef(1000);
  const ping = useRef(null);
  const onAlertRef = useRef(onAlert);
  const onAuthErrRef = useRef(onAuthError);
  useEffect(() => { onAlertRef.current = onAlert; }, [onAlert]);
  useEffect(() => { onAuthErrRef.current = onAuthError; }, [onAuthError]);

  const [wsStatus, setWsStatus] = useState("connecting");
  const [connectedAt, setConnectedAt] = useState(null);

  const connect = useCallback(() => {
    if (dead.current) return;
    setWsStatus("connecting");
    const sock = new WebSocket(url);
    ws.current = sock;

    sock.onopen = () => {
      delay.current = 1000;
      setWsStatus("live");
      setConnectedAt(Date.now());
      ping.current = setInterval(() => {
        if (sock.readyState === WebSocket.OPEN) sock.send("ping");
      }, 30_000);
    };
    sock.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "alert" && msg.data) onAlertRef.current(normaliseAlert(msg.data));
      } catch { /* ignore */ }
    };
    sock.onclose = (ev) => {
      if (ping.current) clearInterval(ping.current);
      if (dead.current) return;
      setWsStatus("reconnecting");
      if (ev.code === 4401) { onAuthErrRef.current?.(); return; }
      setTimeout(connect, delay.current);
      delay.current = Math.min(delay.current * 2, 30_000);
    };
    sock.onerror = () => sock.close();
  }, [url]);

  useEffect(() => {
    if (!enabled || !url) return;
    dead.current = false;
    connect();

    return () => {
      dead.current = true;
      if (ping.current) clearInterval(ping.current);
      ws.current?.close();
      setWsStatus("offline");
    };
  }, [enabled, url, connect]);

  return { wsStatus, connectedAt };
}
